import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export default function Login() {
  const { login, user, loading: authLoading, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionExpired = params.get("reason") === "session_expired";
  const next = params.get("next") || "/dashboard";

  // If the user is already authenticated, send them straight to the dashboard.
  useEffect(() => {
    if (!authLoading && user) navigate(next.startsWith("/") ? next : "/dashboard", { replace: true });
  }, [authLoading, user, next, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate(next.startsWith("/") ? next : "/dashboard");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src="https://static.prod-images.emergentagent.com/jobs/9d29e252-9a73-4ced-9cad-f6f3ec842c8a/images/81a04ae51b7f97e38d3cd615020d86d36a48315920d2938eba5ac1170a18fbcb.png" alt="" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/30 to-transparent"></div>
        <div className="absolute bottom-12 left-12">
          <div className="label-mono text-volt mb-3">// AUTHORIZATION REQUIRED</div>
          <h2 className="font-display text-5xl font-black max-w-md leading-none">REPORT FOR DUTY.</h2>
        </div>
      </div>
      <div className="grid place-items-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="login-form">
          <Link to="/" className="label-mono mb-2 block hover:text-volt">← BACK TO BASE</Link>
          <h1 className="font-display text-4xl font-black mb-2">LOGIN</h1>
          <p className="text-muted-foreground mb-8">Welcome back, operator.</p>

          {sessionExpired && (
            <div className="border border-volt/60 text-volt px-4 py-2 mb-4 text-sm" data-testid="session-expired-banner">
              Your session expired. Sign in again to continue.
            </div>
          )}

          <label className="label-mono block mb-2">Email</label>
          <input
            value={email} onChange={(e) => setEmail(e.target.value)} required type="email"
            className="w-full bg-secondary border border-border px-4 py-3 mb-5 focus:outline-none focus:border-volt"
            data-testid="login-email-input"
          />
          <label className="label-mono block mb-2">Password</label>
          <input
            value={password} onChange={(e) => setPassword(e.target.value)} required type="password"
            className="w-full bg-secondary border border-border px-4 py-3 mb-6 focus:outline-none focus:border-volt"
            data-testid="login-password-input"
          />

          {error && <div className="border border-destructive text-destructive px-4 py-2 mb-4 text-sm" data-testid="login-error">{error}</div>}

          <button disabled={busy} type="submit" className="w-full bg-volt text-black py-4 font-display uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50" data-testid="login-submit-btn">
            {busy ? "Authenticating..." : "Login"}
          </button>

          <div className="mt-6 text-sm text-muted-foreground flex items-center justify-between">
            <Link to="/forgot-password" className="hover:text-volt" data-testid="login-forgot-link">Forgot password?</Link>
            <span>New here? <Link to="/register" className="text-volt hover:underline" data-testid="login-to-register-link">Enlist</Link></span>
          </div>
        </form>
      </div>
    </div>
  );
}
